import * as React from 'react'
import type {EntryPointProps} from 'react-relay'
import type {PreloadedEntryPoint} from 'react-relay/relay-hooks/EntryPointTypes'
import {useParams} from 'react-router-dom'

import {EntityRedirect} from '../../../containers/EntityLink'
import {useBranch} from '../../../hooks/routes'
import {getSingleBuildArg} from '../../../rest/builds'
import HTTPError, {HTTPCodesEnum} from '../../../rest/errors/HTTPError'
import {getProject, getBuild, getBuildType, getBuildsData} from '../../../selectors'
import {restApi} from '../../../services/rest'
import {stringifyId, toBuildId} from '../../../types'
import {extractError} from '../../../utils/extractError'
import {AvailabilityErrorPage} from '../../common/AvailabilityErrorPage/AvailabilityErrorPage'
import {getAvailabilityErrorMessage} from '../../common/AvailabilityErrorPage/AvailabilityErrorPage.utils'
import ErrorPage from '../../common/ErrorPage/ErrorPage'
import Loader from '../../common/Loader/Loader'
import {useRefetchBuildTypeOnPermissionsChange} from '../BuildTypePage/BuildTypePage.hooks'

import type {BuildOverviewTabOuterContainerComponent} from './BuildOverviewTab/BuildOverviewTab.container'
import type {BuildOverviewTabEntryPoint} from './BuildOverviewTab/BuildOverviewTab.entryPoint'
import BuildPage from './BuildPage'
import {getBuildOrPermalinkLocator} from './BuildPage.utils'
import type {BuildTestsTabComponent} from './BuildTestsTab/BuildTestsTab'
import type {BuildTestsTabEntryPoint} from './BuildTestsTab/BuildTestsTab.entryPoint'
import type {DependenciesTabComponent} from './DependenciesTab/DependenciesTab'
import type {DependenciesTabEntryPoint} from './DependenciesTab/DependenciesTab.entryPoint'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  overviewTabEntryPoint?: PreloadedEntryPoint<BuildOverviewTabOuterContainerComponent>
  dependenciesTabEntryPoint?: PreloadedEntryPoint<DependenciesTabComponent>
  testsTabEntryPoint?: PreloadedEntryPoint<BuildTestsTabComponent>
}
function BuildPageContainer(props: Props) {
  const {buildId = '', buildTypeId} = useParams()
  const branch = useBranch()
  const parsedBuildId = toBuildId(buildId)
  const buildLocator = getBuildOrPermalinkLocator(buildId, buildTypeId, branch)
  const {actualBuildId, buildError, buildLoaded, refetch} =
    restApi.endpoints.getBuildNormalizedAsList.useQuery(getSingleBuildArg(buildLocator), {
      selectFromResult: ({data, error}) => ({
        actualBuildId: data?.result[0],
        buildError: error,
        buildLoaded: data != null,
      }),
    })
  const buildState = useAppSelector(state => getBuild(state, actualBuildId)?.state)
  const actualBuildTypeId = useAppSelector(
    state => getBuild(state, actualBuildId)?.buildType ?? buildTypeId,
  )
  const buildIdChanged = !isNaN(parsedBuildId) && buildLoaded && actualBuildId !== parsedBuildId
  const buildTypeIdChanged = actualBuildTypeId !== buildTypeId
  const projectId = useAppSelector(state => getBuildType(state, actualBuildTypeId)?.projectId)
  const queued = useAppSelector(state => getBuild(state, actualBuildId)?.state === 'queued')
  const listError = useAppSelector(
    state => getBuildsData(state, `id:${stringifyId(buildId)}`).error,
  )
  const error = extractError(buildError) || listError
  const projectLoaded = useAppSelector(state => Boolean(getProject(state, projectId)))
  const buildTypeLoaded = useAppSelector(state => getBuildType(state, actualBuildTypeId) != null)

  if (buildTypeIdChanged || buildIdChanged) {
    return (
      <EntityRedirect
        buildId={actualBuildId}
        buildTypeId={actualBuildTypeId}
        redirectedFromId={buildTypeIdChanged ? buildTypeId : undefined}
      />
    )
  }

  if (!window.GLOBAL_ERROR_PAGE_DISABLED && error != null) {
    return (
      <ErrorPage
        buildId={actualBuildId}
        buildTypeId={actualBuildTypeId}
        error={error}
        onRetry={
          error instanceof HTTPError && error.statusCode === HTTPCodesEnum.FORBIDDEN
            ? null
            : refetch
        }
      />
    )
  }

  return actualBuildId != null ? (
    <BuildPage
      buildId={actualBuildId}
      buildLocator={buildLocator}
      buildTypeId={actualBuildTypeId}
      projectId={projectId}
      branch={branch}
      buildState={buildState}
      queued={queued}
      buildLoaded={buildLoaded}
      buildTypeLoaded={buildTypeLoaded}
      projectLoaded={projectLoaded}
      {...props}
    />
  ) : (
    <Loader />
  )
}

export type NestedEntryPoints = {
  overviewTab?: typeof BuildOverviewTabEntryPoint
  dependenciesTab?: typeof DependenciesTabEntryPoint
  testsTab?: typeof BuildTestsTabEntryPoint
}

export default function BuildPageOuterContainer({
  entryPoints,
}: EntryPointProps<{}, NestedEntryPoints, {}, {}>) {
  const {buildId, buildTypeId} = useParams()
  useRefetchBuildTypeOnPermissionsChange(buildTypeId)
  return (
    <AvailabilityErrorPage
      getErrorMessage={error =>
        getAvailabilityErrorMessage({buildId: toBuildId(buildId!), buildTypeId, error})
      }
    >
      <BuildPageContainer
        key={buildId}
        overviewTabEntryPoint={entryPoints.overviewTab}
        dependenciesTabEntryPoint={entryPoints.dependenciesTab}
        testsTabEntryPoint={entryPoints.testsTab}
      />
    </AvailabilityErrorPage>
  )
}
export type BuildPageComponent = typeof BuildPageOuterContainer
